import {Brain, Check, Clapperboard, FileImage, Film, FolderOpen, Heart, Images, Layers, Palette, Pencil, Users, Video, Wallet} from '@nlabs/gothamjs/icons';

interface DiagramStep {
  readonly description: string;
  readonly icon: typeof Images;
  readonly label: string;
}

interface DiagramDefinition {
  readonly caption: string;
  readonly steps: readonly DiagramStep[];
}

export const featureDiagrams: Readonly<Record<string, DiagramDefinition>> = {
  characters: {
    caption: 'One identity, carried into each scene.',
    steps: [
      {description: 'Name, appearance, personality', icon: Users, label: 'Define your character'},
      {description: 'Save a description and reference', icon: FileImage, label: 'Build their identity'},
      {description: 'Assign them to story shots', icon: Clapperboard, label: 'Cast the scene'}
    ]
  },
  credits: {
    caption: 'Planning is free. Generation requires a paid plan and configured providers.',
    steps: [
      {description: 'Zero generation credits', icon: Wallet, label: 'Free account'},
      {description: 'Projects, characters, and shots', icon: Pencil, label: 'Plan your story'},
      {description: 'Generation and media storage', icon: Video, label: 'Paid membership'}
    ]
  },
  elements: {
    caption: 'Save reusable identities for your next campaign.',
    steps: [
      {description: 'Your model or product', icon: FileImage, label: 'Add references'},
      {description: 'A reusable creative identity', icon: Layers, label: 'Create an Element'},
      {description: 'New scenes, looks, and placements', icon: Film, label: 'Build a campaign'}
    ]
  },
  gemini: {
    caption: 'Learn from a reference, develop an original direction, and explore the look with Gemini.',
    steps: [
      {description: 'Video, audio, and visual references', icon: Film, label: 'Understand the footage'},
      {description: 'Research, scenario, and shot prompts', icon: Pencil, label: 'Develop your idea'},
      {description: 'Generate and refine reference images', icon: Images, label: 'Shape the next frame'}
    ]
  },
  memory: {
    caption: 'Past experiences give the next scene context.',
    steps: [
      {description: 'What happened in the story', icon: Clapperboard, label: 'Experience'},
      {description: 'What the character remembers', icon: Brain, label: 'Memory'},
      {description: 'How they feel and respond', icon: Heart, label: 'Emotion & action'}
    ]
  },
  projects: {
    caption: 'A story you can keep developing, before generating a frame.',
    steps: [
      {description: 'The premise and visual direction', icon: Pencil, label: 'Write your brief'},
      {description: 'People who belong in your world', icon: Users, label: 'Create your cast'},
      {description: 'Save the sequence in your project', icon: FolderOpen, label: 'Build your story'}
    ]
  },
  'quick-video': {
    caption: 'Paid workflow: a standalone clip, separate from story projects.',
    steps: [
      {description: 'A single moment or concept', icon: Pencil, label: 'Describe the idea'},
      {description: 'Reference, duration, resolution', icon: FileImage, label: 'Set the direction'},
      {description: 'Generate and review the result', icon: Video, label: 'Create a clip'}
    ]
  },
  seedance: {
    caption: 'Paid workflow: from a starting frame to a moving scene.',
    steps: [
      {description: 'Set the subject and starting look', icon: FileImage, label: 'Reference image'},
      {description: 'Describe motion, camera, atmosphere', icon: Clapperboard, label: 'Your direction'},
      {description: 'A generated scene with sound', icon: Video, label: 'Seedance video'}
    ]
  },
  storyboards: {
    caption: 'Plan the progression. Each shot keeps its own direction and cast.',
    steps: [
      {description: 'Introduce the place and mood', icon: Images, label: '01 · Establish'},
      {description: 'Give your characters something to do', icon: Users, label: '02 · Develop'},
      {description: 'Write the moment that changes things', icon: Clapperboard, label: '03 · Reveal'}
    ]
  },
  style: {
    caption: 'Your project’s visual brief guides each shot’s direction.',
    steps: [
      {description: 'Color, light, and atmosphere', icon: Palette, label: 'Project style'},
      {description: 'Action and camera for this moment', icon: Clapperboard, label: 'Shot direction'},
      {description: 'Shared look, scene-specific detail', icon: Pencil, label: 'Combined prompt'}
    ]
  },
  takes: {
    caption: 'Paid workflow: keep results connected to the shot that created them.',
    steps: [
      {description: 'The results for a story shot', icon: Images, label: 'Review takes'},
      {description: 'Mark the result that fits your story', icon: Check, label: 'Approve a take'},
      {description: 'Organize and download your media', icon: FolderOpen, label: 'Keep it in your library'}
    ]
  }
};
